// Auth + role context. Status states the UI must distinguish (collapsing them
// is how people get confusing sign-in loops — artist-viability lesson):
//   loading | signed_out | wrong_domain | not_in_group | needs_setup |
//   inactive | ready
// A non-domain account is signed out immediately; a domain account with no
// employees/ doc calls provisionSelf once (JIT), which routes it to
// needs_setup or not_in_group. Reads are one-shot getDoc — not live.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  type User,
} from 'firebase/auth'
import { doc, getDoc } from 'firebase/firestore'
import { auth, db, googleProvider, isAllowedEmail } from '../firebase'
import { useEmulators } from '../firebase-config'
import { call } from '../api/callable'
import { CAPABILITIES, type Capability, type Employee, type Role } from '../lib/types'

export type AuthStatus =
  | 'loading'
  | 'signed_out'
  | 'wrong_domain'
  | 'not_in_group'
  | 'needs_setup'
  | 'inactive'
  | 'ready'

interface AuthContextValue {
  status: AuthStatus
  user: User | null
  employee: Employee | null
  role: Role | null
  email: string | null
  can: (capability: Capability) => boolean
  signIn: () => Promise<void>
  /** Emulator-only: password sign-in against seeded accounts (see scripts/seed-emulator.js). */
  signInDev: (email: string) => Promise<void>
  signOutNow: () => Promise<void>
  refresh: () => Promise<void>
  error: string | null
}

const AuthContext = createContext<AuthContextValue | null>(null)

async function loadEmployee(email: string): Promise<Employee | null> {
  const snap = await getDoc(doc(db, 'employees', email))
  return snap.exists() ? (snap.data() as Employee) : null
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading')
  const [user, setUser] = useState<User | null>(null)
  const [employee, setEmployee] = useState<Employee | null>(null)
  const [error, setError] = useState<string | null>(null)

  const resolve = useCallback(async (u: User) => {
    const email = u.email?.toLowerCase() ?? ''
    try {
      let record = await loadEmployee(email)
      if (!record) {
        // First sign-in: JIT provision against the access group.
        const res = await call<{ status: string }>('provisionSelf', {})
        if (res.status === 'not_in_group') {
          setStatus('not_in_group')
          return
        }
        record = await loadEmployee(email)
      }
      if (!record) {
        setStatus('not_in_group')
        return
      }
      setEmployee(record)
      setStatus(record.status === 'active' ? 'ready' : record.status === 'inactive' ? 'inactive' : 'needs_setup')
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      setStatus('not_in_group')
    }
  }, [])

  useEffect(() => {
    return onAuthStateChanged(auth, (u) => {
      setError(null)
      if (!u) {
        setUser(null)
        setEmployee(null)
        setStatus('signed_out')
        return
      }
      if (!isAllowedEmail(u.email)) {
        // Nothing here for outside accounts; leaving the session open is confusing.
        void signOut(auth)
        setStatus('wrong_domain')
        return
      }
      setUser(u)
      setStatus('loading')
      void resolve(u)
    })
  }, [resolve])

  const signIn = useCallback(async () => {
    setError(null)
    try {
      await signInWithPopup(auth, googleProvider())
    } catch (e) {
      const code = (e as { code?: string }).code
      if (code !== 'auth/popup-closed-by-user' && code !== 'auth/cancelled-popup-request') {
        setError(e instanceof Error ? e.message : String(e))
      }
    }
  }, [])

  const signInDev = useCallback(async (email: string) => {
    if (!useEmulators) return
    setError(null)
    try {
      await signInWithEmailAndPassword(auth, email, 'callboard')
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }, [])

  const signOutNow = useCallback(async () => {
    await signOut(auth)
  }, [])

  const refresh = useCallback(async () => {
    if (user) await resolve(user)
  }, [user, resolve])

  const value = useMemo<AuthContextValue>(() => {
    const role = employee?.role ?? null
    return {
      status,
      user,
      employee,
      role,
      email: user?.email?.toLowerCase() ?? null,
      can: (capability) => (role && status === 'ready' ? CAPABILITIES[role].includes(capability) : false),
      signIn,
      signInDev,
      signOutNow,
      refresh,
      error,
    }
  }, [status, user, employee, signIn, signInDev, signOutNow, refresh, error])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}
